import * as Sentry from '@sentry/react';
import { Dispatch } from 'react';
import { Toast } from '../../../components/Common/Toast';
import { CONFIG, StoryStepsEnum } from '../../../config/mainConfig';
import { scrollToArea } from '../../../helper/helper';
import { callAPI } from '../../../services/api.service';
import {
  HandleUpdatePageImageParams,
  UpdateCoverParams,
  UpdatePageImageParams,
  UpdateSummaryParams,
} from '../models/builder.interfaces';
import { UserLocalStorageKeysEnum, UserRoleEnum } from '../../../interfaces/user.interface';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { BookPagesTextValidation } from '../components/StepStoryInformation/StepStoryInformationContainer';

export const updateSummary = async ({
  bookId,
  summaryText,
  setLoader,
  dispatch,
  displayBookPages,
  setBookDataModified,
  closeSummaryModal,
  targetRefs,
}: UpdateSummaryParams) => {
  try {
    setLoader(true);
    await callAPI('api/book-update/update-book-summary', {
      bookId,
      summaryText,
    });

    if (localStorage.getItem('edit-mode') === 'yes') {
      dispatch(setBookDataModified(true));
    }
    displayBookPages();
  } catch (error) {
  } finally {
    closeSummaryModal();
    scrollToArea(targetRefs, 'edit-summary');
  }
};

export const updateCover = async ({ bookId, coverURL, displayBookPages }: UpdateCoverParams) => {
  try {
    await callAPI('api/update-cover', {
      bookId,
      coverURL,
    });
    displayBookPages();
  } catch (error) {}
};

export const handleUpdatePageImage = async ({
  bookData,
  pageIndex,
  imageURL,
  imageURLWebp,
  displayBookPages,
  setLoader,
  setShowImageHistory,
  setStateData,
  targetRefs,
  dispatch,
}: UpdatePageImageParams) => {
  try {
    setLoader(true);
    const response = await callAPI('api/book-update/update-page-image', {
      bookData,
      pageIndex,
      imageURL,
      imageURLWebp,
    });
    if (response.status === HTTPStatusEnum.OK) {
      setShowImageHistory(null);
      setStateData((prevState: any) => ({
        ...prevState,
        showDownloadPDF: true,
        showImageLibrary: false,
      }));
      scrollToArea(targetRefs, pageIndex);
      if (localStorage.getItem(UserLocalStorageKeysEnum.EDIT_MODE) === 'yes') {
        dispatch({ type: 'SET_BOOK_DATA_MODIFIED', payload: true });
      }
    } else {
      Toast('Image Update Failed', true);
    }
    displayBookPages();
  } catch (error) {
    Toast('An error occurred', true);
  } finally {
    setLoader(false);
  }
};

export const handleUpdatePageImageService = async ({
  bookData,
  pageIndex,
  imageURL,
  imageURLWebp,
  displayBookPages,
  setLoader,
  setShowImageHistory,
  setStateData,
  targetRefs,
  dispatch,
}: HandleUpdatePageImageParams) => {
  try {
    setLoader(true);
    const response = await callAPI('api/book-update/update-page-image', {
      bookData,
      pageIndex,
      imageURL,
      imageURLWebp,
    });

    if (response.status === HTTPStatusEnum.OK) {
      setShowImageHistory(null);
      setStateData((prevState: any) => ({
        ...prevState,
        showDownloadPDF: true,
        showImageLibrary: false,
      }));
      scrollToArea(targetRefs, pageIndex);

      if (localStorage.getItem(UserLocalStorageKeysEnum.EDIT_MODE) === 'yes') {
        dispatch({ type: 'SET_BOOK_DATA_MODIFIED', payload: true });
      }
    } else {
      Toast('Image Update Failed', true);
    }

    displayBookPages();
  } catch (error) {
    Toast('An error occurred while updating the image', true);
  } finally {
    setLoader(false);
  }
};

export const updateBookTitle = async (bookId: string, newTitle: string) => {
  try {
    await callAPI('api/update-book-title', { bookId, newTitle });
  } catch (error) {
    throw error;
  }
};

export const updateBookAuthorName = async (bookId: string, authorName: string) => {
  try {
    const response = await callAPI('api/update-book-author', { bookId, authorName });
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('Author Name Updated Successfully');
    } else {
      throw new Error('Author Name Update Failed');
    }
  } catch (error) {
    throw error;
  }
};

export const switchPageImage = async (bookId: string, pageIndex: number, imageURL: string, imageURLWebp?: string) => {
  try {
    const response = await callAPI('api/book-update/switch-page-image', {
      bookId,
      pageIndex: ++pageIndex, // Adjusting the pageIndex as required
      imageURL,
      imageURLWebp: imageURLWebp || imageURL, // Fall back to JPG if webp not available yet
    });

    if (response?.status !== HTTPStatusEnum.OK) {
      throw new Error('Image Switch Failed');
    }

    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSubmitFirstBookStep = async (
  formData: any,
  user: any,
  bookData: any,
  setLoader: (state: boolean) => void,
  setShowLowCreditModal: (state: boolean) => void,
  dispatch: any,
  setStoryData: any,
  setBookId: any,
  setSelectedBookId: any,
  navigateTo: (path: string) => void,
  onValidationError: (validation: BookPagesTextValidation) => void,
  onSuccess: (bookId: string) => void,
) => {
  setLoader(true);

  const creditsRequired = ((parseInt(formData.pageTotal) - 6) / 2 + 1) * CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE;

  if (creditsRequired > user?.plan?.credits && user?.plan?.name !== CONFIG.PROMO_PLAN) {
    Toast('Your current plan does not have enough Image Credits to create this story', true);
    setLoader(false);
    navigateTo('/myPlans');
    return;
  }

  const successFunction = (data: any) => {
    dispatch(
      setStoryData({
        creditsPerBookLimit: data?.creditsPerBookLimit || bookData?.creditsPerBookLimit,
        ...(formData?.pages ? { pages: formData.pages } : {}),
      }),
    );
    dispatch(setBookId(data.bookId));
    localStorage.setItem(UserLocalStorageKeysEnum.BOOK_ID, data?.bookId);
    setSelectedBookId(data.bookId);
    setLoader(false);
  };

  try {
    const requestData: any = {
      email: user?.email,
      formData,
    };
    if (user.role === UserRoleEnum.teacher) {
      requestData.classroomId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_ID);
      requestData.projectId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID);
    }
    const data = await callAPI('api/createNewBook', requestData);

    if (data?.status === HTTPStatusEnum.OK) {
      successFunction(data);
      onSuccess(data.bookId);
    } else if (data.status === HTTPStatusEnum.BAD_REQUEST || data.status === HTTPStatusEnum.FORBIDDEN) {
      setLoader(false);
      Toast(data?.error, true);
      if (data.status === HTTPStatusEnum.FORBIDDEN) setShowLowCreditModal(true);
      if (data.validation) {
        onValidationError && onValidationError(data.validation);
      }
    }
  } catch (error) {
    setLoader(false);
  }
};

export const handleSubmitStepOne = async (
  formData: any,
  user: any,
  bookData: any,
  setLoader: (state: boolean) => void,
  setShowLowCreditModal: (state: boolean) => void,
  dispatch: any,
  setStoryData: any,
  setBookId: any,
  setCurrentStoryStep: any,
  setSelectedBookId: any,
  startStepTwo: (bookId: string) => void,
  navigateTo: (path: string) => void,
  onValidationError?: (validation: BookPagesTextValidation) => void,
) => {
  setLoader(true);

  const creditsRequired = ((parseInt(formData.pageTotal) - 6) / 2 + 1) * CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE;

  if (creditsRequired > user?.plan?.credits && user?.plan?.name !== CONFIG.PROMO_PLAN) {
    Toast('Your current plan does not have enough Image Credits to create this story', true);
    setLoader(false);
    navigateTo('/myPlans');
    return;
  }

  const successFunction = (data: any) => {
    dispatch(
      setStoryData({
        creditsPerBookLimit: data?.creditsPerBookLimit || bookData?.creditsPerBookLimit,
        ...(formData?.pages ? { pages: formData.pages } : {}),
      }),
    );
    dispatch(setBookId(data.bookId));
    localStorage.setItem(UserLocalStorageKeysEnum.BOOK_ID, data?.bookId);
    setCurrentStoryStep(CONFIG.STORY_STEPS.CHARACTER_BUILD);
    setSelectedBookId(data.bookId);
    startStepTwo(data.bookId);
    setLoader(false);
  };

  try {
    const requestData: any = {
      email: user?.email,
      formData,
    };
    if (user.role === UserRoleEnum.teacher) {
      requestData.classroomId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_ID);
      requestData.projectId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID);
    }
    const data = await callAPI('api/saveFormData', requestData);

    if (data?.status === HTTPStatusEnum.OK) {
      successFunction(data);
    } else if (data.status === HTTPStatusEnum.BAD_REQUEST || data.status === HTTPStatusEnum.FORBIDDEN) {
      setLoader(false);
      Toast(data?.error, true);
      if (data.status === HTTPStatusEnum.FORBIDDEN) setShowLowCreditModal(true);
      if (data.validation) {
        onValidationError && onValidationError(data.validation);
      }
    }
  } catch (error) {
    setLoader(false);
  }
};

export const handleStepThree = async (
  bookId: string,
  stateData: any,
  currentPortrait: string,
  dispatch: any,
  setStoryData: any,
  setCurrentStoryStep: any,
  setCurrentStep: any,
  setBookTitle: (title: string) => void,
  storyData: any,
) => {
  if (!bookId) {
    return;
  }

  const t0 = Date.now();
  let outcome: 'success' | 'null_response' | 'error_response' | 'exception' = 'success';

  try {
    const magicResponse = await callAPI('api/magic', {
      bookId,
      existingCharacterId: stateData?.characterId,
      existingCharacterPortrait: currentPortrait,
    });

    const durationMs = Date.now() - t0;

    // callAPI returns null on network error, timeout, or connection reset
    if (!magicResponse || magicResponse?.error) {
      outcome = magicResponse === null ? 'null_response' : 'error_response';
      const errorMsg = magicResponse?.message || 'Unexpected Error. Please Try Again!';
      Toast(errorMsg, true);
      dispatch(
        setStoryData({
          ...storyData,
          storybuildingError: true,
        }),
      );
      setCurrentStoryStep(CONFIG.STORY_STEPS.CHARACTER_BUILD);
      dispatch(setCurrentStep(2));
    } else {
      setBookTitle(magicResponse?.story?.title);

      // Detect partial generation: server returned "success" but some pages
      // still have empty scene strings (from a truncated OpenAI response).
      // Surface this as an error so the user gets retry/back options instead
      // of being stuck on the loading screen forever.
      const pages = magicResponse?.story?.pages;
      if (pages) {
        const hasEmptyScenes = Object.values(pages).some(
          (page: any) => !page?.scene,
        );
        if (hasEmptyScenes) {
          outcome = 'error_response';
          Toast('Story generation was incomplete. Please try again.', true);
          dispatch(
            setStoryData({
              ...storyData,
              storybuildingError: true,
            }),
          );
        }
      }
    }

    // Log EVERY magic call outcome to Sentry (success and failure)
    // Truncate response to 8KB to stay within Sentry's 200KB event limit.
    // Base64-encoded so Sentry's content scrubber doesn't redact nested fields like "email".
    const responseSnapshot = (() => {
      try {
        const s = JSON.stringify(magicResponse);
        const truncated = s.length > 8000 ? s.slice(0, 8000) + '…[truncated]' : s;
        return btoa(unescape(encodeURIComponent(truncated)));
      } catch { return String(magicResponse); }
    })();

    Sentry.captureMessage(`magic:${outcome}`, {
      level: outcome === 'success' ? 'info' : 'error',
      tags: { flow: 'handleStepThree', outcome, bookId },
      extra: {
        bookId,
        durationMs,
        magic_response_b64: responseSnapshot,
        status: magicResponse?.status,
        title: magicResponse?.story?.title,
        pageCount: magicResponse?.story?.pages ? Object.keys(magicResponse.story.pages).length : null,
        errorMessage: magicResponse?.error || magicResponse?.message || null,
        wasNull: magicResponse === null,
      },
    });
  } catch (error: any) {
    outcome = 'exception';
    Sentry.captureException(error, {
      tags: { flow: 'handleStepThree', outcome, bookId },
      extra: { bookId, durationMs: Date.now() - t0 },
    });
    Toast('Unexpected Error. Please Try Again!', true);
    dispatch(
      setStoryData({
        ...storyData,
        storybuildingError: true,
      }),
    );
    dispatch(setCurrentStep(2));
  }
};

export const startStepSix = async (
  bookId: string,
  selectedCoverUrl: string | null,
  bookData: any,
  dispatch: any,
  setCurrentStep: (step: number) => void,
  setCurrentStoryStep: (step: StoryStepsEnum | null) => void,
) => {
  try {
    const coverURL = selectedCoverUrl || bookData?.pages?.page1?.imageURL;
    await callAPI('api/update-cover', { bookId, coverURL });
    dispatch(setCurrentStep(6));
    setCurrentStoryStep(CONFIG.STORY_STEPS.PURCHASE_DOWNLOAD);
  } catch (error) {}
};

export const generatePdf = async (bookId: string, setLoader: (isLoading: boolean) => void, setPdfUrl: any) => {
  setLoader(true);
  try {
    const result = await callAPI(`api/pdf-generator/pdf?bookId=${bookId}`);
    if (result?.pdfUrl) {
      setPdfUrl(result?.pdfUrl);
    }
  } catch (error) {
  } finally {
    setLoader(false);
  }
};

export const updateBookCover = async (
  bookId: string | undefined,
  coverURL: string | undefined,
  isEditMode: boolean,
  dispatch: Dispatch<any>,
  setBookDataModified: (isModified: boolean) => void,
  setStateData: (state: any) => void,
  displayBookPages: () => void,
) => {
  try {
    await callAPI('api/update-cover', { bookId, coverURL });

    if (isEditMode) {
      dispatch(setBookDataModified(true));
    }

    setStateData((prevState: any) => ({
      ...prevState,
      showDownloadPDF: true,
    }));

    displayBookPages();
  } catch (error) {}
};

export const stopBookCharacterGeneration = async (bookId: string | null | undefined) => {
  try {
    if (!bookId) {
      return;
    }
    await callAPI(`api/book-update/${bookId}/generation/character`, {}, 'DELETE');
  } catch (error) {}
};

export const stopBookPageGeneration = async (bookId: string | null | undefined, pageKey: string) => {
  try {
    if (!bookId) {
      return;
    }
    await callAPI(`api/book-update/${bookId}/generation/page/${pageKey}`, {}, 'DELETE');
  } catch (error) {}
};
